/**
 * UrbanHarvestArticleLayout — shared shell used by every "sharing option"
 * article page (Pick Your Own, Pre-Picked Curbside, Donation Only,
 * Harvest Help Needed).
 *
 * Renders the hero, prose body, pull-quote, closing line, bottom CTA
 * row, and an EntityMessageBoard discussion attached to
 * (entityType="urban_harvest_mode", entityId=<slug>).
 *
 * Each mode has its own page file for future divergence (bespoke
 * content sections, hero images, sub-articles, etc.) while still
 * sharing this consistent chrome today.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import EntityMessageBoard from "@/components/EntityMessageBoard";

export const UH_PALETTE = {
  cream:  "#FBF7EF",
  ink:    "#1C3B1E",
  leaf:   "#3E7C4B",
  moss:   "#245635",
  amber:  "#E4A140",
  orange: "#EA580C",
  peach:  "#FFE9D0",
  sage:   "#DCEAD1",
};

export default function UrbanHarvestArticleLayout({
  slug,             // "pick-your-own" | "pre-picked-curbside" | ...
  title,
  subtitle,
  icon: Icon,
  accent = UH_PALETTE.leaf,
  body = [],        // array of paragraph strings (or React nodes)
  pullquote = null,
  closer = null,
  children = null,  // any extra sections between article + CTA row
}) {
  return (
    <div className="min-h-screen"
         style={{ background: UH_PALETTE.cream, color: UH_PALETTE.ink }}
         data-testid={`urban-harvest-mode-${slug}`}>
      <SeoHead section="food-pulse"
               title={`${title} — Urban Harvest · Food Pulse`}
               description={subtitle} />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b"
               style={{ background: `linear-gradient(135deg, ${UH_PALETTE.moss} 0%, ${accent} 100%)`,
                        borderColor: "#0B2E12" }}>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-white">
          <Link to="/food-pulse/urban-harvest"
                data-testid="uh-mode-back-top"
                className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest opacity-90 hover:opacity-100">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Urban Harvest
          </Link>
          <div className="mt-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full grid place-items-center"
                 style={{ background: "rgba(255,255,255,0.15)" }}>
              {Icon ? <Icon className="w-5 h-5" /> : null}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest"
                 style={{ color: UH_PALETTE.amber }}>
              Food Pulse · Urban Harvest · Sharing option
            </div>
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-6xl leading-tight"
              data-testid="uh-mode-title">
            {title}
          </h1>
          <p className="mt-3 text-lg" style={{ color: UH_PALETTE.peach }}
             data-testid="uh-mode-subtitle">
            {subtitle}
          </p>
        </div>
      </section>

      {/* ── Article body ─────────────────────────────────────── */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-14 space-y-5"
               data-testid="uh-mode-article"
               style={{ fontSize: "1.05rem", lineHeight: 1.75 }}>
        {body.map((para, i) => (
          <p key={i} style={{ color: UH_PALETTE.ink }}>{para}</p>
        ))}

        {pullquote && (
          <blockquote
            data-testid="uh-mode-pullquote"
            className="my-8 border-l-4 pl-5 py-2 font-display italic text-xl sm:text-2xl leading-snug"
            style={{ borderColor: accent, color: UH_PALETTE.moss }}>
            "{pullquote}"
          </blockquote>
        )}

        {closer && (
          <p className="font-semibold" style={{ color: UH_PALETTE.moss }}
             data-testid="uh-mode-closer">
            {closer}
          </p>
        )}

        {children}
      </article>

      {/* ── Discussion board ─────────────────────────────────── */}
      <section className="border-t"
               style={{ background: UH_PALETTE.peach, borderColor: "#F0C79A" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14"
             data-testid="uh-mode-discussion">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
               style={{ color: UH_PALETTE.orange }}>
            Community · Discussion
          </div>
          <h2 className="font-display text-3xl sm:text-4xl mb-3"
              style={{ color: UH_PALETTE.moss }}>
            Share what's working in your community.
          </h2>
          <p className="text-sm opacity-80 mb-6 max-w-2xl">
            Trade tips, coordinate with neighbours, or ask the FoodPulse team a
            question about the <b>{title}</b> option. Every post is public and
            visible to fellow homeowners and volunteers.
          </p>
          <EntityMessageBoard entityType="urban_harvest_mode"
                              entityId={slug}
                              title={`${title} — Discussion`} />
        </div>
      </section>

      {/* ── Bottom CTA row ───────────────────────────────────── */}
      <section className="border-t"
               style={{ background: UH_PALETTE.ink, borderColor: "#0B2E12", color: "#fff" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 text-center">
          <div className="font-display text-2xl sm:text-3xl mb-3">
            Ready to join the Urban Harvest?
          </div>
          <p className="text-sm opacity-80 max-w-xl mx-auto mb-6">
            Register your tree or garden in under a minute, or suggest one you've spotted nearby.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/food-pulse/urban-harvest#register"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold hover:shadow-lg transition-shadow"
                  style={{ background: UH_PALETTE.amber, color: UH_PALETTE.moss }}
                  data-testid="uh-mode-cta-register">
              Register your tree / garden <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/food-pulse/urban-harvest#suggest"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border-2 border-white/40 hover:bg-white/10 transition-colors"
                  data-testid="uh-mode-cta-suggest">
              Suggest one nearby
            </Link>
            <Link to="/food-pulse/urban-harvest"
                  className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest opacity-80 hover:opacity-100"
                  data-testid="uh-mode-back-bottom">
              <ArrowLeft className="w-3.5 h-3.5" /> All Urban Harvest options
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
